/**
 * Home (tap any time + rolling 2-hour window), timed session, and session summary.
 */
import { repo } from '../../data/repo';
import { track } from '../../data/track';
import { newId } from '../../data/localStore';
import { startOfLocalDay, pregnancyInfo } from '../../domain/pregnancy';
import { firstCompletion, sessionOutcome, TARGET_KICKS, WINDOW_MS, windowState } from '../../domain/countToTen';
import { streakDays } from '../../domain/stats';
import { hasPremium, type CountSession, type Kick, type Zone } from '../../domain/types';
import { fmtDuration, fmtTime, t } from '../../i18n';
import { sfx } from '../../audio/sfx';
import { $, closeSheet, confirmSheet, esc, icons, openSheet, toast, vibrate } from '../dom';
import { go, register, type Screen } from '../router';
import { bellyRemoveLast, mountBelly, unmountBelly } from '../bellyHost';
import { ambient } from '../../audio/ambient';
import { runCoach } from '../coach';
import { openJournal } from '../journalSheet';

const RING_C = 2 * Math.PI * 28;

function ring(count: number, done: boolean): string {
  const frac = Math.min(1, count / TARGET_KICKS);
  return `<div class="ring ${done ? 'done' : ''}"><svg viewBox="0 0 64 64"><circle class="track" cx="32" cy="32" r="28" fill="none" stroke-width="7"/>
    <circle class="bar" cx="32" cy="32" r="28" fill="none" stroke-width="7" stroke-linecap="round" stroke-dasharray="${RING_C}" stroke-dashoffset="${RING_C * (1 - frac)}"/></svg>
    <span class="lbl">${done ? '✓' : count}</span></div>`;
}

const todayKicks = (): Kick[] => {
  const s = startOfLocalDay(Date.now());
  return repo.kicks.filter(k => k.occurredAt >= s);
};

const markerPoints = (kicks: Kick[]) =>
  kicks.filter(k => k.x !== null && k.y !== null && k.z !== null).map(k => ({ x: k.x!, y: k.y!, z: k.z! }));

const GUEST_NUDGE_AFTER = 10;

function banner(): string {
  if (repo.mode === 'demo') return `<div class="banner">${esc(t('common', 'demoBanner'))}</div>`;
  if (!navigator.onLine || repo.syncProblem === 'offline') return `<div class="banner offline">${esc(t('common', 'offline'))}</div>`;
  // Trying it without an account: after she has used it a little, offer to keep it safe. Never before.
  if (repo.guest && repo.kicks.length >= GUEST_NUDGE_AFTER)
    return `<button class="banner banner-save" data-action="go" data-to="account">${esc(t('account', 'guestNudge'))} <b>${esc(t('account', 'guestNudgeCta'))}</b></button>`;
  return '';
}

function planBadge(): string {
  const e = repo.entitlement;
  if (!e) return '';
  if (e.status === 'trial') {
    const left = Math.max(0, Math.ceil(((e.trialEndsAt ?? 0) - repo.now()) / 864e5));
    return `<button class="badge badge-trial" data-action="go" data-to="paywall">${esc(t('profile', 'trialLeft', { n: left }))}</button>`;
  }
  if (e.status === 'paid') return `<span class="badge badge-premium">✓ Premium</span>`;
  return `<button class="badge badge-free" data-action="go" data-to="paywall">${esc(t('profile', 'free'))} · ${esc(t('stats', 'unlock'))}</button>`;
}

// ================================================================== HOME
let tickTimer: number | undefined;

function statusHtml(): string {
  const times = repo.kicks.map(k => k.occurredAt);
  const now = Date.now();
  const st = windowState(times, now);
  const todayDone = firstCompletion(times, startOfLocalDay(now), now + 1);
  if (st.kind === 'reached') {
    return `${ring(10, true)}<div><h3>${esc(t('home', 'reachedTitle'))}</h3><p>${esc(t('home', 'reachedLead', { m: st.completion.minutes }))}</p></div>`;
  }
  if (st.kind === 'counting') {
    return `${ring(st.count, false)}<div><h3>${esc(t('home', 'countingTitle', { n: st.count }))}</h3><p>${esc(t('home', 'countingLead', { t: fmtDuration(st.msLeft) }))}</p></div>`;
  }
  return `${ring(0, !!todayDone)}<div><h3>${esc(todayDone ? t('home', 'todayDone') : t('home', 'idleTitle'))}</h3><p>${esc(t('home', 'idleLead'))}</p></div>`;
}

function chipsHtml(): string {
  const n = todayKicks().length;
  const s = streakDays(repo.kicks, Date.now());
  return `<span class="pill">${esc(t('home', 'todayCount', { n }))}</span>${s > 1 ? `<span class="pill ok">${esc(t('home', 'streak', { n: s }))}</span>` : ''}`;
}

const home: Screen = {
  id: 'home', nav: 'home', root: true,
  render: () => {
    const p = repo.profile!;
    const preg = pregnancyInfo(p.dueDate);
    const h = new Date().getHours();
    const greet = t('home', h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening');
    const who = p.babyNickname ? t('home', 'whoBaby', { name: p.displayName, baby: p.babyNickname }) : t('home', 'who', { name: p.displayName });
    return `
    <div class="home-head">
      <div><div class="hello">${esc(greet)} · ${esc(t('common', 'week'))} ${preg.week}</div><div class="who">${esc(who)}</div></div>
      <div id="plan-badge">${planBadge()}</div>
    </div>
    <div id="home-banner">${banner()}</div>
    <div class="card status" id="status-card" style="margin-top:8px">${statusHtml()}</div>
    <div class="stage-wrap home-stage-wrap">
      <div class="belly-stage" id="home-stage"></div>
      <button class="icon-btn sound-hint" data-action="toggle-sound" aria-label="sound">${p.soundOn ? icons.sound : icons.mute}</button>
    </div>
    <div class="home-bottom">
    <div class="chips" id="home-chips" style="justify-content:center">${chipsHtml()}</div>
    <div class="home-actions">
      <button class="icon-btn" data-action="undo-tap" aria-label="${esc(t('home', 'undo'))}" style="height:50px;width:50px">${icons.undo}</button>
      <button class="btn btn-ghost jot-btn" id="btn-jot" data-action="open-journal">${icons.pen.replace('<svg', '<svg width="18" height="18"')}${esc(t('journal', 'button'))}</button>
      <button class="btn btn-ghost" id="btn-focus" data-action="start-session">${icons.timer.replace('<svg', '<svg width="20" height="20"')}${esc(t('home', 'focus'))}</button>
    </div>
    ${helpLink()}
    </div>`;
  },
  mount(root) {
    sfx.setEnabled(repo.profile?.soundOn ?? true);
    ambient.want(true);
    const premium = hasPremium(repo.entitlement);
    void mountBelly($('#home-stage', root)!, (hit) => {
      const k = repo.addKick({
        occurredAt: Date.now(), source: 'tap', sessionClientId: null,
        zone: premium && hit ? hit.zone : null,
        x: premium && hit ? hit.x : null, y: premium && hit ? hit.y : null, z: premium && hit ? hit.z : null
      });
      const times = repo.kicks.map(x => x.occurredAt);
      const st = windowState(times, Date.now());
      const n = st.kind === 'counting' ? st.count : st.kind === 'reached' ? TARGET_KICKS : 1;
      // celebrate exactly when this tap completes 10-in-2h
      const justReached = st.kind === 'reached' && st.completion.at === k.occurredAt;
      if (justReached) { sfx.complete(); vibrate([30, 60, 30]); } else { sfx.kick(n); vibrate(20); }
      track.daily('kick_day');
      home.refresh!();
      maybeNudgeJot();
    }, { spatial: premium, markers: premium ? markerPoints(todayKicks()) : [] });

    window.clearInterval(tickTimer);
    tickTimer = window.setInterval(() => home.refresh!(), 30_000);

    // first visit: trial-ended paywall (once), then coach marks (once)
    const e = repo.entitlement;
    if (e?.status === 'free' && e.trialEndsAt && !repo.ui.trialPaywallSeen && repo.kicks.length) {
      repo.setUi({ trialPaywallSeen: true });
      setTimeout(() => go('paywall'), 300);
    } else if (!repo.ui.coachHome) {
      setTimeout(() => runCoach([
        { sel: '#home-stage', title: t('coach', 's1t'), text: t('coach', 's1') },
        { sel: '#status-card', title: t('coach', 's2t'), text: t('coach', 's2') },
        { sel: '#btn-focus', title: t('coach', 's3t'), text: t('coach', 's3') }
      ], () => repo.setUi({ coachHome: true })), 500);
    }
  },
  unmount() { window.clearInterval(tickTimer); window.clearTimeout(nudgeTimer); unmountBelly(); ambient.want(false); },
  refresh() {
    const s = $('#status-card'); if (s) s.innerHTML = statusHtml();
    const c = $('#home-chips'); if (c) c.innerHTML = chipsHtml();
    const b = $('#plan-badge'); if (b) b.innerHTML = planBadge();
    const bn = $('#home-banner'); if (bn) bn.innerHTML = banner();
  },
  actions: {
    'open-help': () => showHelp(),
    'open-journal': () => openJournal(),
    'undo-tap': () => {
      const last = todayKicks().filter(k => k.source === 'tap').sort((a, b) => b.occurredAt - a.occurredAt)[0];
      if (!last) return;
      repo.removeKick(last.clientId);
      if (last.x !== null) bellyRemoveLast();
      sfx.undo();
      toast(t('home', 'undone'));
      home.refresh!();
    },
    'start-session': () => go('session'),
    'toggle-sound': el => {
      const p = repo.profile!;
      const on = !p.soundOn;
      repo.saveProfile({ ...p, soundOn: on });
      sfx.setEnabled(on);
      el.innerHTML = on ? icons.sound : icons.mute;
      if (on) sfx.tap();
    }
  }
};

// ================================================================== TIMED SESSION
let session: CountSession | null = null;
let sessionKicks: Kick[] = [];
let timer: number | undefined;
let lastSaved: CountSession | null = null;

function renderCounter() {
  const n = sessionKicks.length;
  const el = $('#kick-n'); if (el) el.textContent = String(n);
  const dots = $('#kick-dots');
  if (dots) dots.innerHTML = Array.from({ length: TARGET_KICKS }, (_, i) => `<i class="${i < n ? 'on' : ''}"></i>`).join('');
}

function tickSession() {
  if (!session) return;
  const ms = Date.now() - session.startedAt;
  const h = Math.floor(ms / 3_600_000), m = Math.floor(ms / 60_000) % 60, s = Math.floor(ms / 1000) % 60;
  const p = (x: number) => String(x).padStart(2, '0');
  const el = $('#timer'); if (el) el.textContent = h ? `${h}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
  if (ms >= WINDOW_MS && sessionKicks.length < TARGET_KICKS) finishSession();
}

function finishSession() {
  if (!session) return;
  window.clearInterval(timer);
  const end = Date.now();
  const out = sessionOutcome(session.startedAt, sessionKicks.map(k => k.occurredAt), end);
  lastSaved = { ...session, endedAt: end, kickCount: sessionKicks.length, reachedTarget: out.reachedTarget, lowMovementFlag: false };
  repo.saveSession(lastSaved);
  session = null;
  go('done', { replace: true });
}

function discardSession() {
  window.clearInterval(timer);
  if (session) {
    sessionKicks.forEach(k => repo.removeKick(k.clientId));
    repo.saveSession({ ...session, endedAt: Date.now(), kickCount: 0, note: null });
  }
  session = null; sessionKicks = [];
  go('home', { replace: true });
}

/**
 * General guidance, opened only when the mother asks for it. It never looks at
 * her numbers: the app is a journal, not a detector (DECISIONS D6).
 */
export function showHelp(): void {
  const phone = repo.profile?.hospitalPhone;
  openSheet(`
    <div style="text-align:center">
      <div class="rf-icon" style="background:var(--amber-soft);color:#A86A22">💛</div>
      <h2>${esc(t('help', 'title'))}</h2>
      <p style="margin:8px 0 16px;font-size:14px">${esc(t('help', 'body'))}</p>
      <div class="stack">
        ${phone
          ? `<a class="btn btn-primary btn-block" href="tel:${esc(phone.replace(/[^0-9+]/g, ''))}" style="text-decoration:none">${icons.phone.replace('<svg', '<svg width="18" height="18"')} ${esc(t('help', 'callHospital'))}</a>
             <div class="phone-chip" style="margin:0 auto;font-size:18px;background:var(--bg-soft);color:var(--fg)">${esc(phone)}</div>`
          : `<button class="btn btn-primary btn-block" data-action="help-add-phone">${esc(t('help', 'addHospital'))}</button>`}
        <a class="link-btn" href="tel:1669" style="text-align:center">${esc(t('help', 'emergency'))}</a>
        <button class="btn btn-ghost btn-block" data-action="sheet-close">${esc(t('common', 'close'))}</button>
      </div>
      <p class="small muted" style="margin-top:12px">${esc(t('help', 'disclaimer'))}</p>
    </div>`, { 'help-add-phone': () => { closeSheet(); go('profile'); setTimeout(() => document.querySelector<HTMLElement>('[data-field="hospital"]')?.click(), 250); } },
  { center: true, className: 'help-sheet' });
}

/**
 * Teach the journal at the moment it makes sense: right after a kick.
 * Shown on the first few taps only, and never again once she has saved a note.
 */
const JOT_NUDGES = 5;
let nudgeTimer: number | undefined;
function maybeNudgeJot(): void {
  const ui = repo.ui;
  const shown = ui.jotNudges ?? 0;
  if (ui.jotLearned || shown >= JOT_NUDGES || document.getElementById('coach')) return;
  const btn = $('#btn-jot'); const row = btn?.parentElement;
  if (!btn || !row) return;
  repo.setUi({ jotNudges: shown + 1 });   // Dew: the button wiggles on each of the first five taps
  btn.classList.remove('nudge'); void btn.offsetWidth; btn.classList.add('nudge');
  let bubble = $('#jot-bubble') as HTMLButtonElement | null;
  if (!bubble) {
    bubble = document.createElement('button');
    bubble.id = 'jot-bubble'; bubble.className = 'jot-bubble'; bubble.dataset.action = 'open-journal';
    row.appendChild(bubble);
  }
  bubble.textContent = t('journal', 'nudge');
  bubble.style.left = `${btn.offsetLeft}px`;
  if (bubble.hidden) {   // pop in when it appears; while it's up, later taps just keep it a little longer
    bubble.hidden = false;
    bubble.style.animation = 'none'; void bubble.offsetWidth; bubble.style.animation = '';
  }
  window.clearTimeout(nudgeTimer);
  nudgeTimer = window.setTimeout(() => { if (bubble) bubble.hidden = true; }, 4500);
}

const helpLink = () => `<button class="link-btn help-link" data-action="open-help">${esc(t('help', 'link'))}</button>`;

const sessionScreen: Screen = {
  id: 'session',
  render: () => `
    <div class="track-top">
      <button class="back-btn" data-action="session-leave" aria-label="${esc(t('common', 'close'))}">${icons.close}</button>
      <div class="timer num" id="timer">00:00</div>
      <button class="back-btn" data-action="toggle-sound-s" aria-label="sound">${repo.profile?.soundOn ? icons.sound : icons.mute}</button>
    </div>
    <div class="counter">
      <span class="n num" id="kick-n">0</span><span class="of num"> / ${TARGET_KICKS}</span>
      <div class="dots" id="kick-dots"></div>
    </div>
    <div class="stage-wrap" style="flex:1;display:flex;flex-direction:column">
      <div class="belly-stage" id="session-stage"></div>
      <div class="stage-hint">${esc(t('session', 'tapHint'))}</div>
    </div>
    <div class="track-actions screen-pad-bottom">
      <button class="btn btn-ghost" data-action="session-undo">${icons.undo.replace('<svg', '<svg width="18" height="18"')}</button>
      <button class="btn btn-primary" data-action="session-end">${esc(t('session', 'end'))}</button>
    </div>`,
  mount(root) {
    if (!session) {
      session = { clientId: newId(), startedAt: Date.now(), endedAt: null, kickCount: 0, reachedTarget: false, lowMovementFlag: false, note: null };
      sessionKicks = [];
      repo.saveSession(session);
    }
    renderCounter();
    const premium = hasPremium(repo.entitlement);
    void mountBelly($('#session-stage', root)!, (hit) => {
      if (!session || sessionKicks.length >= TARGET_KICKS) return;
      const k = repo.addKick({
        occurredAt: Date.now(), source: 'session', sessionClientId: session.clientId,
        zone: premium && hit ? hit.zone : null, x: premium && hit ? hit.x : null, y: premium && hit ? hit.y : null, z: premium && hit ? hit.z : null
      });
      sessionKicks.push(k);
      renderCounter();
      if (sessionKicks.length >= TARGET_KICKS) { sfx.complete(); vibrate([30, 60, 30]); setTimeout(finishSession, 700); }
      else { sfx.kick(sessionKicks.length); vibrate(20); }
    }, { spatial: premium, markers: premium ? markerPoints(sessionKicks) : [] });
    window.clearInterval(timer);
    timer = window.setInterval(tickSession, 1000);
    tickSession();
  },
  unmount() { unmountBelly(); },
  actions: {
    'session-undo': () => {
      const k = sessionKicks.pop(); if (!k) return;
      repo.removeKick(k.clientId);
      if (k.x !== null) bellyRemoveLast();
      sfx.undo(); renderCounter();
    },
    'session-end': () => {
      if (!sessionKicks.length) { confirmSheet(t('session', 'emptyEnd'), '', t('session', 'leaveOk'), t('common', 'cancel'), discardSession); return; }
      finishSession();
    },
    'session-leave': () => {
      if (!sessionKicks.length) { discardSession(); return; }
      confirmSheet(t('session', 'leave'), t('session', 'leaveLead', { n: sessionKicks.length }), t('session', 'leaveOk'), t('common', 'cancel'), discardSession, true);
    },
    'toggle-sound-s': el => {
      const p = repo.profile!; const on = !p.soundOn;
      repo.saveProfile({ ...p, soundOn: on }); sfx.setEnabled(on);
      el.innerHTML = on ? icons.sound : icons.mute;
    }
  }
};

// ================================================================== DONE
function topZone(kicks: Kick[]): Zone | null {
  const c: Partial<Record<Zone, number>> = {};
  kicks.forEach(k => { if (k.zone) c[k.zone] = (c[k.zone] ?? 0) + 1; });
  const best = (Object.entries(c) as [Zone, number][]).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : null;
}

const done: Screen = {
  id: 'done', root: true,
  render: () => {
    const s = lastSaved!;
    const mins = Math.max(1, Math.round(((s.endedAt ?? Date.now()) - s.startedAt) / 60000));
    const z = topZone(sessionKicks);
    const premium = hasPremium(repo.entitlement);
    return `
    <div class="spacer"></div>
    <div class="done-ring">${s.reachedTarget ? '🎉' : '📝'}</div>
    <h2 style="text-align:center">${esc(s.reachedTarget ? t('session', 'doneTitle') : t('session', 'partialTitle', { n: s.kickCount }))}</h2>
    <p class="muted" style="text-align:center;margin-bottom:18px">${esc(s.reachedTarget ? t('session', 'doneLead', { m: mins }) : t('session', 'partialLead'))}</p>
    <div class="card"><div class="summary-grid">
      <div><div class="v num">${s.kickCount}</div><div class="k">${esc(t('common', 'times'))}</div></div>
      <div><div class="v num">${mins}</div><div class="k">${esc(t('session', 'mins'))}</div></div>
      <div><div class="v" style="font-size:14px;line-height:30px">${premium ? esc(z ? t('zones', z) : '–') : '🔒'}</div><div class="k">${esc(t('session', 'mainZone'))}</div></div>
    </div></div>
    <div class="field" style="margin-top:14px"><label for="done-note">${esc(t('session', 'note'))}</label>
      <input class="note-input" id="done-note" maxlength="200" placeholder="${esc(t('session', 'notePh'))}"></div>
    <p class="small muted" style="text-align:center;margin-top:10px">${esc(fmtTime(s.startedAt))}</p>
    <div style="text-align:center;margin-top:6px">${helpLink()}</div>
    <div class="spacer"></div>
    <div class="screen-pad-bottom" style="padding-top:16px"><button class="btn btn-primary btn-block" data-action="done-finish">${esc(t('session', 'backHome'))}</button></div>`;
  },
  mount() { if (!lastSaved) go('home', { replace: true }); },
  actions: {
    'open-help': () => showHelp(),
    'done-finish': () => {
      const note = ($('#done-note') as HTMLInputElement).value.trim();
      if (lastSaved && note) repo.saveSession({ ...lastSaved, note });
      sessionKicks = [];
      go('home', { replace: true });
    }
  }
};

register(home, sessionScreen, done);
